import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const WHATSAPP_NUMBER = "905072200492";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Merhaba, FINORA lansmana özel fiyat teklifi hakkında bilgi almak istiyorum.")}`;

export default function LaunchBanner() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} data-testid="launch-banner" className="relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/images/renders/render12.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1A1A1A]/75" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="md:col-span-7"
          >
            {/* Badge */}
            <div className="inline-block bg-[#c13a05] px-4 py-2 mb-6">
              <p className="font-sub text-[11px] text-white font-bold tracking-[0.25em]">LANSMAN ÖZEL</p>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white font-medium leading-tight mb-4">
              Ön Satış Dönemine Özel<br />
              <span className="italic text-[#d5bd9a]">Avantajlı Fiyatlar</span>
            </h2>

            <p className="font-body text-white/60 text-base leading-relaxed font-light max-w-lg mb-8">
              FINORA'nın ön satış dönemi başladı. Sınırlı sayıda daire için özel lansman
              fiyatları ve esnek ödeme koşullarıyla hayalinizdeki Akdeniz yaşamına şimdi adım atın.
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-0 mb-8">
              {[
                { value: "430", label: "DAİRE" },
                { value: "4", label: "TİP" },
                { value: "0", unit: "m", label: "DENİZE" },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex-1 text-center py-4 ${i < 2 ? "border-r border-white/15" : ""}`}
                >
                  <p className="font-number text-2xl md:text-3xl text-white">
                    {item.value}
                    {item.unit && <span className="text-base font-body font-light text-white/50 ml-1">{item.unit}</span>}
                  </p>
                  <p className="font-sub text-[9px] text-white/40 tracking-[0.2em] font-bold mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-5"
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10 text-center">
              <img src="/images/brandmarks/brandmark-beyaz.svg" alt="" className="w-10 mx-auto mb-5 opacity-40" />

              <p className="font-heading text-xl text-white font-normal mb-2">Fırsatı Kaçırmayın</p>
              <p className="font-body text-sm text-white/50 font-light mb-6">
                Lansman fiyatları sınırlı sayıda daire için geçerlidir.
              </p>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="banner-whatsapp-btn"
                className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1fba59] text-white font-sub text-[11px] tracking-[0.15em] font-bold py-4 transition-all duration-300 mb-4"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                HEMEN BİLGİ ALIN
              </a>

              <button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="w-full font-sub text-[11px] tracking-[0.15em] font-bold text-white/50 hover:text-white border border-white/15 hover:border-white/30 py-3 transition-all duration-300"
              >
                FORM İLE İLETİŞİME GEÇİN
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
